import { currentLocale, i18n } from "@/i18n";
import type { Locale } from "@rec/shared";
import {
  chapterSetInfoSchema,
  draftChapterSchema,
  recordingExportInfoSchema,
  studioProjectInfoSchema,
  summaryVersionInfoSchema,
  transcriptTranslationInfoSchema,
  timelineSchema,
  apiErrorSchema,
  meetingSummarySchema,
  recordingDetailSchema,
  recordingSummarySchema,
  normalizeRole,
  searchHitSchema,
  transcriptSchema,
  type RecordingDetail,
  type RecordingSummary,
  type Role,
  type SearchHit,
  type Transcript,
  type DraftChapter,
  type PublishProjectInput,
  type Timeline,
} from "@rec/shared";
import { z } from "zod";

/**
 * A role as read from the API.
 *
 * Lenient on purpose: the web and the API are separate services and deploy
 * separately, so for a minute or two a new client talks to an old API — one
 * that still says `manager`. Rejecting that spelling would take down the
 * whole People page rather than mislabel one row, and `normalizeRole` maps it
 * to the right role anyway.
 */
const apiRole = z.string().transform(normalizeRole);

/**
 * API client.
 *
 * Responses are parsed with the shared Zod schemas rather than cast, so a
 * backend change surfaces here as a clear error instead of `undefined` reaching
 * a render and blanking the page.
 */

/**
 * Where the API lives.
 *
 * In production the app and the API are different subdomains, so a relative
 * `/api` resolves against the app's own nginx — which has no such location and
 * serves index.html with a 200. Every call would then "succeed" and die parsing
 * HTML as JSON. `lib/auth.ts` already derives its base this way; this is the
 * same rule for the rest of the client.
 */
export const API_BASE = import.meta.env.VITE_API_URL
  ? String(import.meta.env.VITE_API_URL).replace(/\/$/, "")
  : "/api";

const BASE = API_BASE;

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }
}

/**
 * A sentence for a response that carried no error body of ours.
 *
 * `response.statusText` used to be the fallback, so a user pressing Generate
 * with no model configured was shown the literal words "Internal Server Error".
 * That is a protocol detail, not an explanation, and it tells them nothing about
 * what to do — which is the same complaint that produced the honest status
 * badges.
 */
function httpMessage(status: number): string {
  if (status === 401) return i18n.t("shell:http.unauthorized");
  if (status === 403) return i18n.t("shell:http.forbidden");
  if (status === 404) return i18n.t("shell:http.notFound");
  if (status === 429) return i18n.t("shell:http.tooManyRequests");
  if (status >= 500) return i18n.t("shell:http.server");
  return i18n.t("shell:http.rejected");
}

async function request<S extends z.ZodTypeAny>(
  path: string,
  schema: S,
  init?: RequestInit,
): Promise<z.infer<S>> {
  const response = await fetch(`${BASE}${path}`, {
    ...init,
    // Sessions are cookie-based, so every call must carry them.
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      // So anything the server writes in this request's name — an invitation
      // email, say — goes out in the language the person is using.
      "Accept-Language": currentLocale(),
      ...init?.headers,
    },
  });

  if (!response.ok) {
    const body = await response.json().catch(() => null);
    const parsed = apiErrorSchema.safeParse(body);
    throw new ApiError(
      response.status,
      parsed.success ? parsed.data.error.code : "unknown",
      parsed.success ? parsed.data.error.message : httpMessage(response.status),
    );
  }

  return schema.parse(await response.json());
}

export const api = {
  listRecordings: (): Promise<RecordingSummary[]> =>
    request("/recordings", z.object({ recordings: z.array(recordingSummarySchema) })).then(
      (r) => r.recordings,
    ),

  getRecording: (id: string): Promise<RecordingDetail> =>
    request(`/recordings/${id}`, recordingDetailSchema),

  getTranscript: (id: string): Promise<Transcript> =>
    request(`/recordings/${id}/transcript`, transcriptSchema),

  /**
   * Name a speaker. One request renames every line they said, because the
   * transcript resolves the label by join rather than storing a copy per row.
   */
  renameSpeaker: (recordingId: string, clusterId: string, label: string) =>
    request(
      `/recordings/${recordingId}/speakers/${clusterId}`,
      z.object({ ok: z.boolean() }),
      { method: "PATCH", body: JSON.stringify({ label }) },
    ),

  /**
   * Attribute one line to a different speaker. Pass `newSpeakerLabel` instead
   * of `clusterId` when the right speaker does not exist yet — which is the
   * case whenever diarization merged two people into one voice.
   */
  reassignUtterance: (
    recordingId: string,
    utteranceId: string,
    target: { clusterId: string } | { newSpeakerLabel: string },
  ) =>
    request(
      `/recordings/${recordingId}/utterances/${utteranceId}`,
      z.object({ ok: z.boolean(), clusterId: z.string() }),
      { method: "PATCH", body: JSON.stringify(target) },
    ),

  createMeeting: (input: {
    meetingUrl: string;
    title?: string;
    projectId?: string | null;
    joinAt?: string;
  }) =>
    request(
      "/recordings",
      z.object({
        meetingId: z.string(),
        recordingId: z.string().optional(),
        botName: z.string(),
        joinAt: z.string().nullable(),
      }),
      { method: "POST", body: JSON.stringify(input) },
    ),

  getSummary: (id: string) =>
    request(
      `/recordings/${id}/summary`,
      z.object({ summary: meetingSummarySchema.nullable() }),
    ).then((r) => r.summary),

  /** Ask for a summary. Returns immediately; the result arrives by polling. */
  generateSummary: (id: string) =>
    request(`/recordings/${id}/summary`, z.object({ ok: z.boolean() }), {
      method: "POST",
    }),

  generateChapters: (id: string) =>
    request(`/recordings/${id}/chapters`, z.object({ ok: z.boolean() }), {
      method: "POST",
    }),

  /**
   * Stop generating chapters.
   *
   * Cannot un-send a request already with the model, so a call already in
   * flight may still be charged — but nothing is written and the panel returns
   * to idle immediately.
   */
  cancelChapters: (id: string) =>
    request(`/recordings/${id}/chapters/cancel`, z.object({ ok: z.boolean() }), {
      method: "POST",
    }),

  /**
   * Re-run transcription for an upload that has none.
   *
   * The endpoint existed and nothing called it, so an upload stored before an
   * ASR key was configured stayed permanently transcript-less — the badge said
   * "Partial", the panel said "it appears once processing finishes", and
   * processing had finished.
   */
  transcribeRecording: (id: string) =>
    request(`/recordings/${id}/transcribe`, z.object({ ok: z.boolean() }), {
      method: "POST",
    }),

  /**
   * Rename a recording. The title belongs to the meeting, so this also renames
   * it in the library, in search results and in the player.
   */
  renameRecording: (id: string, title: string) =>
    request(`/recordings/${id}`, z.object({ ok: z.boolean(), title: z.string() }), {
      method: "PATCH",
      body: JSON.stringify({ title }),
    }),

  /**
   * Encode a recording for adaptive streaming.
   *
   * Without a ladder the player pulls the whole original file before the first
   * frame, which on a large upload is a long wait for nothing.
   */
  transcodeRecording: (id: string, force = false) =>
    request(
      `/recordings/${id}/transcode${force ? "?force=true" : ""}`,
      z.object({ ok: z.boolean() }),
      { method: "POST" },
    ),

  /** End a live recording early — the bot leaves and the media is finalised. */
  stopRecording: (id: string) =>
    request(`/recordings/${id}/stop`, z.object({ ok: z.boolean() }), {
      method: "POST",
    }),

  deleteRecording: (id: string) =>
    request(`/recordings/${id}`, z.object({ ok: z.boolean() }), {
      method: "DELETE",
    }),

  // ─── Folders ───────────────────────────────────────────────────────────────

  listProjects: () =>
    request(
      "/projects",
      z.object({
        projects: z.array(
          z.object({
            id: z.string(),
            parentId: z.string().nullable(),
            name: z.string(),
            description: z.string().nullable(),
            createdAt: z.string(),
            recordingCount: z.number(),
          }),
        ),
      }),
    ).then((r) => r.projects),

  createProject: (input: { name: string; parentId?: string | null }) =>
    request(
      "/projects",
      z.object({ project: z.object({ id: z.string(), name: z.string() }) }),
      { method: "POST", body: JSON.stringify(input) },
    ),

  moveProject: (id: string, parentId: string | null) =>
    request(`/projects/${id}/parent`, z.object({ ok: z.boolean() }), {
      method: "PUT",
      body: JSON.stringify({ parentId }),
    }),

  moveRecording: (recordingId: string, projectId: string | null) =>
    request(`/projects/assign/${recordingId}`, z.object({ ok: z.boolean() }), {
      method: "PUT",
      body: JSON.stringify({ projectId }),
    }),

  // ─── Uploads ───────────────────────────────────────────────────────────────

  presignUpload: (input: {
    filename: string;
    contentType: string;
    sizeBytes: number;
    durationMs?: number;
    projectId?: string | null;
  }) =>
    request(
      "/uploads/presign",
      z.object({
        recordingId: z.string(),
        key: z.string(),
        uploadId: z.string(),
        partBytes: z.number(),
      }),
      { method: "POST", body: JSON.stringify(input) },
    ),

  /** Sign a batch of parts. Signed on demand — these expire. */
  signUploadParts: (recordingId: string, partNumbers: number[]) =>
    request(
      `/uploads/${recordingId}/parts`,
      z.object({
        urls: z.array(z.object({ partNumber: z.number(), url: z.string().url() })),
      }),
      { method: "POST", body: JSON.stringify({ partNumbers }) },
    ).then((r) => r.urls),

  /** What S3 already holds, so a resumed upload can skip it. */
  listUploadedParts: (recordingId: string) =>
    request(
      `/uploads/${recordingId}/parts`,
      z.object({
        parts: z.array(
          z.object({
            partNumber: z.number(),
            etag: z.string(),
            size: z.number(),
          }),
        ),
        partBytes: z.number(),
      }),
    ),

  completeUpload: (recordingId: string) =>
    request(`/uploads/${recordingId}/complete`, z.object({ ok: z.boolean() }), {
      method: "POST",
    }),

  /** Discard an upload. Uploaded parts are billed until this runs. */
  abortUpload: (recordingId: string) =>
    request(`/uploads/${recordingId}/abort`, z.object({ ok: z.boolean() }), {
      method: "POST",
    }),

  // ─── Imports ───────────────────────────────────────────────────────────────

  /** Queue an import from a link. The server fetches it; nothing streams through here. */
  importFromLink: (input: { url: string; title?: string; projectId?: string | null }) =>
    request(
      "/imports",
      z.object({ recordingId: z.string(), source: z.string(), url: z.string() }),
      { method: "POST", body: JSON.stringify(input) },
    ),

  /** Fetch a failed import again — after the owner made the video public, say. */
  retryImport: (recordingId: string) =>
    request(`/imports/${recordingId}/retry`, z.object({ ok: z.boolean() }), {
      method: "POST",
    }),

  // ─── Studio ────────────────────────────────────────────────────────────────

  listStudioProjects: () =>
    request("/studio/projects", z.object({ projects: z.array(studioProjectInfoSchema) })).then(
      (r) => r.projects,
    ),

  createStudioProject: (input: { title?: string; sourceRecordingId?: string }) =>
    request(
      "/studio/projects",
      z.object({ project: studioProjectInfoSchema }),
      { method: "POST", body: JSON.stringify(input) },
    ).then((r) => r.project),

  getStudioProject: (id: string) =>
    request(
      `/studio/projects/${id}`,
      z.object({
        project: studioProjectInfoSchema.extend({
          timeline: timelineSchema,
          draftChapters: z.array(draftChapterSchema).default([]),
          sourceLabels: z.record(z.string(), z.string()).default({}),
        }),
      }),
    ).then((r) => r.project),

  saveStudioProject: (
    id: string,
    input: {
      title?: string;
      timeline: Timeline;
      draftChapters: DraftChapter[];
      sourceLabels?: Record<string, string>;
    },
  ) =>
    request(`/studio/projects/${id}`, z.object({ ok: z.boolean() }), {
      method: "PUT",
      body: JSON.stringify(input),
    }),

  deleteStudioProject: (id: string) =>
    request(`/studio/projects/${id}`, z.object({ ok: z.boolean() }), {
      method: "DELETE",
    }),

  publishStudioProject: (id: string, input: PublishProjectInput) =>
    request(
      `/studio/projects/${id}/publish`,
      z.object({ mode: z.enum(["chapters", "render"]), recordingId: z.string().uuid() }),
      { method: "POST", body: JSON.stringify(input) },
    ),

  // ─── Version history ───────────────────────────────────────────────────────

  listChapterSets: (recordingId: string) =>
    request(
      `/recordings/${recordingId}/chapter-sets`,
      z.object({ sets: z.array(chapterSetInfoSchema) }),
    ).then((r) => r.sets),

  restoreChapterSet: (recordingId: string, setId: string) =>
    request(
      `/recordings/${recordingId}/chapter-sets/${setId}/restore`,
      z.object({ ok: z.boolean() }),
      { method: "POST" },
    ),

  listSummaryVersions: (recordingId: string) =>
    request(
      `/recordings/${recordingId}/summary-versions`,
      z.object({ versions: z.array(summaryVersionInfoSchema) }),
    ).then((r) => r.versions),

  restoreSummaryVersion: (recordingId: string, versionId: string) =>
    request(
      `/recordings/${recordingId}/summary-versions/${versionId}/restore`,
      z.object({ ok: z.boolean() }),
      { method: "POST" },
    ),

  // ─── Translations, downloads and exports ───────────────────────────────────

  listTranslations: (recordingId: string) =>
    request(
      `/recordings/${recordingId}/translations`,
      z.object({
        /** What the transcript is already in, when detectable. */
        sourceLanguage: z.string().nullable(),
        translations: z.array(transcriptTranslationInfoSchema),
      }),
    ),

  requestTranslation: (recordingId: string, language: string) =>
    request(
      `/recordings/${recordingId}/translations`,
      z.object({ id: z.string().uuid(), status: z.string() }),
      { method: "POST", body: JSON.stringify({ language }) },
    ),

  /** Cancel a running translation (stops the spending) or discard a stored one. */
  cancelTranslation: (recordingId: string, language: string) =>
    request(
      `/recordings/${recordingId}/translations/${language}`,
      z.object({ ok: z.boolean() }),
      { method: "DELETE" },
    ),

  getTranslation: (recordingId: string, language: string) =>
    request(
      `/recordings/${recordingId}/translations/${language}`,
      z.object({
        id: z.string().uuid(),
        language: z.string(),
        status: z.string(),
        error: z.string().nullable(),
        utterances: z.array(z.object({ utteranceId: z.string(), text: z.string() })),
        chapters: z
          .array(
            z.object({
              chapterId: z.string(),
              title: z.string(),
              summary: z.string(),
            }),
          )
          .default([]),
      }),
    ),

  /** What deleting this recording would break, for the confirm dialog. */
  getRecordingReferences: (recordingId: string) =>
    request(
      `/recordings/${recordingId}/references`,
      z.object({ edits: z.number().int(), drafts: z.number().int() }),
    ),

  /** A one-hour presigned link that downloads with the meeting's title. */
  getDownloadUrl: (recordingId: string) =>
    request(`/recordings/${recordingId}/download`, z.object({ url: z.string() })).then(
      (r) => r.url,
    ),

  listExports: (recordingId: string) =>
    request(
      `/recordings/${recordingId}/exports`,
      z.object({ exports: z.array(recordingExportInfoSchema) }),
    ).then((r) => r.exports),

  requestExport: (recordingId: string, subtitleLanguage: string) =>
    request(
      `/recordings/${recordingId}/exports`,
      z.object({ id: z.string().uuid(), status: z.string() }),
      { method: "POST", body: JSON.stringify({ subtitleLanguage }) },
    ),

  getExportDownloadUrl: (recordingId: string, exportId: string) =>
    request(
      `/recordings/${recordingId}/exports/${exportId}/download`,
      z.object({ url: z.string() }),
    ).then((r) => r.url),

  // ─── Search ────────────────────────────────────────────────────────────────

  search: (q: string): Promise<SearchHit[]> =>
    request(
      `/search?q=${encodeURIComponent(q)}`,
      z.object({ hits: z.array(searchHitSchema), query: z.string() }),
    ).then((r) => r.hits),

  // ─── Settings ──────────────────────────────────────────────────────────────

  getSettings: () =>
    request(
      "/settings",
      z.object({
        activeProvider: z.string().nullable(),
        botDisplayName: z.string(),
        retentionDays: z.string().nullable(),
        credentials: z.array(
          z.object({
            provider: z.string(),
            model: z.string(),
            keyHint: z.string(),
            lastVerifiedAt: z.string().nullable(),
            lastError: z.string().nullable(),
          }),
        ),
      }),
    ),

  getModels: (provider: string) =>
    request(
      `/settings/models/${provider}`,
      z.object({
        models: z.array(
          z.object({
            id: z.string(),
            name: z.string(),
            contextWindow: z.number().nullable(),
            inputPricePerMTok: z.number().nullable(),
            outputPricePerMTok: z.number().nullable(),
            isFree: z.boolean(),
            description: z.string().nullable(),
          }),
        ),
      }),
    ).then((r) => r.models),

  saveCredential: (input: { provider: string; apiKey: string; model: string }) =>
    request("/settings/credentials", z.object({ ok: z.boolean() }), {
      method: "PUT",
      body: JSON.stringify(input),
    }),

  /**
   * Workspace settings: the notetaker's name and how long recordings are kept.
   *
   * The endpoint existed and no client method called it, so both were read-only
   * in the UI — retention in particular was fetched, never rendered, and
   * therefore impossible to configure at all.
   */
  updateSettings: (input: { botDisplayName?: string; retentionDays?: number | null }) =>
    request("/settings", z.object({ ok: z.boolean() }), {
      method: "PATCH",
      body: JSON.stringify(input),
    }),

  /** Stop using a stored key. The credential row is deleted, not disabled. */
  removeCredential: (provider: string) =>
    request(`/settings/credentials/${provider}`, z.object({ ok: z.boolean() }), {
      method: "DELETE",
    }),

  // ─── Chat ──────────────────────────────────────────────────────────────────

  listChats: (recordingId: string) =>
    request(
      `/recordings/${recordingId}/chats`,
      z.object({
        chats: z.array(
          z.object({
            id: z.string(),
            title: z.string().nullable(),
            updatedAt: z.string(),
          }),
        ),
      }),
    ).then((r) => r.chats),

  getChat: (chatId: string) =>
    request(
      `/chats/${chatId}/messages`,
      z.object({
        chat: z.object({ id: z.string(), title: z.string().nullable() }),
        messages: z.array(
          z.object({
            id: z.string(),
            role: z.enum(["user", "assistant"]),
            content: z.string(),
            // Stored as jsonb, so an older row may not match today's shape.
            // Falling back to none keeps a readable message readable.
            citations: z
              .array(
                z.object({
                  utteranceId: z.string(),
                  startMs: z.number(),
                  speakerLabel: z.string(),
                  snippet: z.string(),
                }),
              )
              .catch([]),
            createdAt: z.string(),
          }),
        ),
      }),
    ),

  renameChat: (chatId: string, title: string) =>
    request(`/chats/${chatId}`, z.object({ ok: z.boolean(), title: z.string() }), {
      method: "PATCH",
      body: JSON.stringify({ title }),
    }),

  deleteChat: (chatId: string) =>
    request(`/chats/${chatId}`, z.object({ ok: z.boolean() }), { method: "DELETE" }),

  /** Removes the message, and its answer when the message is a question. */
  deleteChatMessage: (chatId: string, messageId: string) =>
    request(
      `/chats/${chatId}/messages/${messageId}`,
      z.object({ ok: z.boolean(), deleted: z.array(z.string()) }),
      { method: "DELETE" },
    ),

  /**
   * Who the caller is, independent of any workspace.
   *
   * Needed before a workspace is chosen: a platform operator belongs to no
   * tenant, so anything tenant-scoped cannot answer whether to show them the
   * back-office.
   */
  me: () =>
    request(
      "/me",
      z.object({
        id: z.string(),
        email: z.string(),
        name: z.string().nullable(),
        isPlatformOperator: z.boolean(),
        /** The saved interface language; null until the person has one. */
        locale: z.string().nullable().default(null),
      }),
    ),

  /** Save the interface language to the account, so it follows the person. */
  updateMyLocale: (locale: Locale) =>
    request("/me", z.object({ ok: z.boolean() }), {
      method: "PATCH",
      body: JSON.stringify({ locale }),
    }),

  // ─── Invitations (tenant admins) ───────────────────────────────────────────

  listInvitations: () =>
    request(
      "/members/invitations",
      z.object({
        invitations: z.array(
          z.object({
            id: z.string(),
            email: z.string(),
            role: apiRole,
            inviterName: z.string().nullable(),
            expiresAt: z.string(),
            expired: z.boolean(),
          }),
        ),
      }),
    ).then((r) => r.invitations),

  inviteMember: (input: { email: string; role: Role }) =>
    request(
      "/members/invitations",
      z.object({ ok: z.boolean(), invitationId: z.string(), resent: z.boolean() }),
      { method: "POST", body: JSON.stringify(input) },
    ),

  revokeInvitation: (id: string) =>
    request(`/members/invitations/${id}`, z.object({ ok: z.boolean() }), {
      method: "DELETE",
    }),

  // ─── Accepting an invitation (no account yet) ──────────────────────────────

  getInvitation: (token: string) =>
    request(
      `/invitations/${token}`,
      z.object({
        kind: z.enum(["platform", "tenant"]),
        email: z.string(),
        name: z.string().nullable(),
        note: z.string().nullable(),
        organizationName: z.string().nullable(),
        expired: z.boolean(),
        used: z.boolean(),
      }),
    ),

  acceptInvitation: (
    token: string,
    input: { name: string; password: string; workspaceName?: string },
  ) =>
    request(`/invitations/${token}/accept`, z.object({ ok: z.boolean() }), {
      method: "POST",
      body: JSON.stringify(input),
    }),

  // ─── Platform invitations (operators) ──────────────────────────────────────

  listPlatformInvitations: () =>
    request(
      "/admin/platform-invitations",
      z.object({
        invitations: z.array(
          z.object({
            id: z.string(),
            email: z.string(),
            name: z.string().nullable(),
            status: z.string(),
            note: z.string().nullable(),
            organizationName: z.string().nullable(),
            expiresAt: z.string(),
            acceptedAt: z.string().nullable(),
            createdAt: z.string(),
            expired: z.boolean(),
          }),
        ),
      }),
    ).then((r) => r.invitations),

  sendPlatformInvitation: (input: { email: string; name?: string; note?: string }) =>
    request(
      "/admin/platform-invitations",
      z.object({ ok: z.boolean(), resent: z.boolean() }),
      { method: "POST", body: JSON.stringify(input) },
    ),

  revokePlatformInvitation: (id: string) =>
    request(`/admin/platform-invitations/${id}`, z.object({ ok: z.boolean() }), {
      method: "DELETE",
    }),

  // ─── Back-office (platform operators) ──────────────────────────────────────

  listTenants: () =>
    request(
      "/admin/tenants",
      z.object({
        tenants: z.array(
          z.object({
            id: z.string(),
            name: z.string(),
            slug: z.string(),
            createdAt: z.string(),
            blocked: z.boolean(),
            blockedReason: z.string().nullable(),
            members: z.number(),
            recordings: z.number(),
            storageBytes: z.number(),
            lastActivityAt: z.string().nullable(),
          }),
        ),
      }),
    ).then((r) => r.tenants),

  renameTenant: (id: string, name: string) =>
    request(`/admin/tenants/${id}`, z.object({ ok: z.boolean() }), {
      method: "PATCH",
      body: JSON.stringify({ name }),
    }),

  blockTenant: (id: string, blocked: boolean, reason?: string) =>
    request(`/admin/tenants/${id}/block`, z.object({ ok: z.boolean() }), {
      method: "POST",
      body: JSON.stringify({ blocked, reason }),
    }),

  listTenantMembers: (id: string) =>
    request(
      `/admin/tenants/${id}/members`,
      z.object({
        members: z.array(
          z.object({
            memberId: z.string(),
            userId: z.string(),
            name: z.string().nullable(),
            email: z.string(),
            role: z.string(),
            banned: z.boolean(),
            joinedAt: z.string(),
          }),
        ),
      }),
    ).then((r) => r.members),

  listPlatformUsers: (q?: string) =>
    request(
      `/admin/users${q ? `?q=${encodeURIComponent(q)}` : ""}`,
      z.object({
        users: z.array(
          z.object({
            id: z.string(),
            name: z.string().nullable(),
            email: z.string(),
            isOperator: z.boolean(),
            banned: z.boolean(),
            banReason: z.string().nullable(),
            createdAt: z.string(),
            memberships: z.array(
              z.object({ id: z.string(), name: z.string(), role: z.string() }),
            ),
          }),
        ),
      }),
    ).then((r) => r.users),

  banUser: (id: string, banned: boolean, reason?: string) =>
    request(`/admin/users/${id}/ban`, z.object({ ok: z.boolean() }), {
      method: "POST",
      body: JSON.stringify({ banned, reason }),
    }),

  setOperator: (id: string, operator: boolean) =>
    request(`/admin/users/${id}/operator`, z.object({ ok: z.boolean() }), {
      method: "POST",
      body: JSON.stringify({ operator }),
    }),

  renameProject: (id: string, name: string) =>
    request(`/projects/${id}`, z.object({ ok: z.boolean() }), {
      method: "PATCH",
      body: JSON.stringify({ name }),
    }),

  /**
   * Delete a folder.
   *
   * Its children rise to the root rather than disappearing — `parent_id` is
   * ON DELETE SET NULL — so this never silently removes a tree.
   */
  deleteProject: (id: string) =>
    request(`/projects/${id}`, z.object({ ok: z.boolean() }), { method: "DELETE" }),

  // ─── Members ───────────────────────────────────────────────────────────────

  listMembers: () =>
    request(
      "/members",
      z.object({
        members: z.array(
          z.object({
            id: z.string(),
            userId: z.string(),
            email: z.string(),
            name: z.string(),
            role: apiRole,
            banned: z.boolean(),
            twoFactorEnabled: z.boolean(),
            joinedAt: z.string(),
            canManage: z.boolean(),
          }),
        ),
        assignableRoles: z.array(apiRole),
      }),
    ),

  changeMemberRole: (memberId: string, role: Role, confirmDangerous?: boolean) =>
    request(`/members/${memberId}/role`, z.object({ ok: z.boolean() }), {
      method: "PATCH",
      body: JSON.stringify({ role, confirmDangerous }),
    }),

  removeMember: (memberId: string, confirmDangerous?: boolean) =>
    request(
      `/members/${memberId}${confirmDangerous ? "?confirmDangerous=true" : ""}`,
      z.object({ ok: z.boolean() }),
      { method: "DELETE" },
    ),

  // ─── Back-office ───────────────────────────────────────────────────────────

  listAccessRequests: (status: string) =>
    request(
      `/admin/access-requests?status=${status}`,
      z.object({
        requests: z.array(
          z.object({
            id: z.string(),
            email: z.string(),
            name: z.string(),
            companyName: z.string(),
            message: z.string().nullable(),
            status: z.string(),
            createdAt: z.string(),
          }),
        ),
      }),
    ).then((r) => r.requests),

  approveAccessRequest: (
    id: string,
    input: { organizationName: string; slug: string },
  ) =>
    request(`/admin/access-requests/${id}/approve`, z.object({ ok: z.boolean() }), {
      method: "POST",
      body: JSON.stringify(input),
    }),

  rejectAccessRequest: (id: string, note?: string) =>
    request(`/admin/access-requests/${id}/reject`, z.object({ ok: z.boolean() }), {
      method: "POST",
      body: JSON.stringify({ note }),
    }),
};
